// src/hooks/useFreshLocation.ts
import { useCallback } from "react";
import { useLocation as useStoredLocation } from "./useLocation";
import { requestLocationPermission as requestPermissionViaBrowser } from "../utils/privacy";
import { Location } from "../types";

/**
 * Returns a helper that gets a fresh GPS fix, falling back to the cached
 * location from useLocation if the fresh fix fails or times out.
 *
 * Previously this dance was inlined in 5 different places in ChatInterface.
 * Keep it here so the fallback behavior and null-safety are consistent.
 */
export const useFreshLocation = () => {
  const { location: cachedLocation } = useStoredLocation();

  const getFreshLocation = useCallback(async (): Promise<Location | null> => {
    try {
      const position = await requestPermissionViaBrowser();
      return {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: position.timestamp,
      };
    } catch (error) {
      console.log(
        "Could not get fresh location, falling back to cached:",
        error
      );
      return cachedLocation;
    }
  }, [cachedLocation]);

  return { getFreshLocation, cachedLocation };
};
